import {
  emptyEntityFrontmatter,
  type EntityFrontmatter,
} from "../entityFrontmatter";
import { isJust, just, nothing, type Maybe } from "../maybe";
import { demoProjectRoot } from "../platform/projectRoot";
import type { UnplacedItem } from "../tauri/unplaced";
import {
  defaultZwConfig,
  type ManuscriptItem,
  type ZwChapter,
} from "../zwConfig";
import {
  insertCommentMarker,
  loadLittleWomenSource,
  parseLittleWomenChapters,
  splitBodyIntoScenes,
  type LittleWomenChapter,
} from "./content/parseLittleWomen";

export type DemoHistoryEntry = {
  oid: string;
  message: string;
  time: number;
  before: string;
  after: string;
};

export type DemoDeletedEntry = {
  relativePath: string;
  oid: string;
  message: string;
  time: number;
  content: string;
};

export type DemoFixtureSeed = {
  rootPath: string;
  config: ReturnType<typeof defaultZwConfig>;
  files: Record<string, string>;
  databaseFields: Record<string, EntityFrontmatter>;
  graphLayout: Record<string, { x: number; y: number }>;
  history: Record<string, DemoHistoryEntry[]>;
  deleted: DemoDeletedEntry[];
  unplaced: UnplacedItem[];
};

type CommentEntry = {
  createdAt: string;
  resolved: boolean;
  messages: Array<{
    id: string;
    body: string;
    createdAt: string;
    authorName: string;
  }>;
};

const COMMENTS: Record<string, CommentEntry> = {
  c_demo1: {
    createdAt: "2026-08-11T04:00:00.000Z",
    resolved: false,
    messages: [
      {
        id: "m_demo1",
        body: "Keep this opener. Instant Jo.",
        createdAt: "2026-08-11T04:00:00.000Z",
        authorName: "You",
      },
    ],
  },
  c_demo2: {
    createdAt: "2026-08-11T04:12:00.000Z",
    resolved: false,
    messages: [
      {
        id: "m_demo2",
        body: "Breakfast scene earns the presents payoff.",
        createdAt: "2026-08-11T04:12:00.000Z",
        authorName: "You",
      },
    ],
  },
  c_demo3: {
    createdAt: "2026-08-11T04:18:00.000Z",
    resolved: false,
    messages: [
      {
        id: "m_demo3a",
        body: "Jo's refusal lands hard here.",
        createdAt: "2026-08-11T04:18:00.000Z",
        authorName: "You",
      },
      {
        id: "m_demo3b",
        body: "Maybe trim before the next beat?",
        createdAt: "2026-08-11T04:19:00.000Z",
        authorName: "You",
      },
    ],
  },
  c_demo4: {
    createdAt: "2026-08-11T04:25:00.000Z",
    resolved: false,
    messages: [
      {
        id: "m_demo4",
        body: "Laurie waiting in the lane. Gut punch.",
        createdAt: "2026-08-11T04:25:00.000Z",
        authorName: "You",
      },
    ],
  },
};

/** Showcase chapters get multiple scenes + comment anchors. */
const SCENE_PLAN: Record<
  string,
  {
    files: string[];
    comments?: Array<{ file: string; id: string; needle: string }>;
  }
> = {
  "Playing Pilgrims": {
    files: ["scene-a0001.md", "scene-a0002.md", "scene-a0003.md"],
    comments: [
      {
        file: "scene-a0001.md",
        id: "c_demo1",
        needle: "grumbled Jo",
      },
    ],
  },
  "A Merry Christmas": {
    files: ["scene-b0001.md", "scene-b0002.md"],
    comments: [
      {
        file: "scene-b0001.md",
        id: "c_demo2",
        needle: "Merry Christmas",
      },
    ],
  },
  Heartache: {
    files: ["scene-c0001.md", "scene-c0002.md", "scene-c0003.md"],
    comments: [
      {
        file: "scene-c0001.md",
        id: "c_demo3",
        needle: "I can't",
      },
      {
        file: "scene-c0003.md",
        id: "c_demo4",
        needle: "friend",
      },
    ],
  },
};

function commentsBlock(ids: string[]): string {
  const store: Record<string, CommentEntry> = {};
  for (const id of ids) {
    const entry = COMMENTS[id];
    if (typeof entry !== "undefined") store[id] = entry;
  }
  if (Object.keys(store).length === 0) return "";
  return `\n\n<!-- zw-comments\n${JSON.stringify(store, null, 2)}\n-->`;
}

function chapter(
  folder: string,
  scenes: Array<{ file: string; synopsis: string }>,
  synopsis = "",
): ZwChapter {
  return {
    folder,
    name: folder,
    index: "index.md",
    scenes: scenes.map((s) => ({ file: s.file, synopsis: s.synopsis })),
    synopsis,
  };
}

function sceneFilesFor(title: string, index: number): string[] {
  return (
    SCENE_PLAN[title]?.files ?? [
      `scene-${String(index + 1).padStart(4, "0")}.md`,
    ]
  );
}

function buildSceneMarkdown(
  sceneBody: string,
  commentIds: string[] = [],
): string {
  return sceneBody.trimEnd() + commentsBlock(commentIds);
}

function buildChapterScenes(
  source: LittleWomenChapter,
  chapterIndex: number,
): {
  chapter: ZwChapter;
  files: Record<string, string>;
} {
  const filesSpec = sceneFilesFor(source.title, chapterIndex);
  let bodies = splitBodyIntoScenes(source.body, filesSpec.length);
  if (bodies.length < filesSpec.length) {
    while (bodies.length < filesSpec.length) bodies.push("…");
  } else if (bodies.length > filesSpec.length) {
    const head = bodies.slice(0, filesSpec.length - 1);
    const tail = bodies.slice(filesSpec.length - 1).join("\n\n");
    bodies = [...head, tail];
  }

  const plan = SCENE_PLAN[source.title];
  const files: Record<string, string> = {};
  const scenes: Array<{ file: string; synopsis: string }> = [];

  filesSpec.forEach((file, i) => {
    let body = bodies[i] ?? "";
    const commentIds: string[] = [];
    for (const c of plan?.comments ?? []) {
      if (c.file !== file) continue;
      if (body.includes(c.needle)) {
        body = insertCommentMarker(body, c.needle, c.id);
      } else {
        // Scene splits can move a needle; still surface the comment UI.
        const token = body.match(/\S+/)?.[0];
        if (typeof token !== "undefined" && token !== "") {
          body = insertCommentMarker(body, token, c.id);
        }
      }
      commentIds.push(c.id);
    }
    const rel = `Chapters/${source.title}/${file}`;
    files[rel] = buildSceneMarkdown(body, commentIds);
    scenes.push({
      file,
      synopsis:
        i === 0
          ? `Chapter: ${source.title}`
          : `Continued: ${source.title} (${i + 1})`,
    });
  });

  return {
    chapter: chapter(
      source.title,
      scenes,
      source.part === "first" ? "Part First" : "Part Second",
    ),
    files,
  };
}

let cachedSeed: Maybe<DemoFixtureSeed> = nothing;

/** Build (and memoize) the in-memory Little Women demo project. */
export async function buildDemoFixture(): Promise<DemoFixtureSeed> {
  if (isJust(cachedSeed)) return cachedSeed.value;

  const source = await loadLittleWomenSource();
  const parsed = parseLittleWomenChapters(source);

  const sceneFiles: Record<string, string> = {};
  const firstChapters: ZwChapter[] = [];
  const secondChapters: ZwChapter[] = [];

  parsed.forEach((ch, index) => {
    const built = buildChapterScenes(ch, index);
    Object.assign(sceneFiles, built.files);
    if (ch.part === "first") firstChapters.push(built.chapter);
    else secondChapters.push(built.chapter);
  });

  const manuscript: ManuscriptItem[] = [
    {
      type: "act",
      folder: "Part First",
      name: "Part First",
      index: "index.md",
      chapters: firstChapters,
    },
    {
      type: "act",
      folder: "Part Second",
      name: "Part Second",
      index: "index.md",
      chapters: secondChapters,
    },
  ];

  const config = defaultZwConfig(manuscript);
  // This named manuscript predates automatic numbering.
  config.chapterNumbering = { pattern: "name", style: "arabic", prefix: true };
  config.actNumbering = { pattern: "name", style: "roman", prefix: true };
  config.metadata.title = "Little Women";
  config.metadata.author = "Louisa May Alcott";
  config.metadata.description =
    "Demo manuscript: the full public-domain text of Little Women (Project Gutenberg #37106), arranged into acts, chapters, and scenes.";
  config.goals.sessionWords = 500;
  config.goals.dailyWords = 2000;
  config.goals.projectWords = 50_000;

  const parkedName = "scene-d0001.md";
  const parkedBody =
    "## Cut scene\n\nA scrap set aside from an earlier outline pass of the March sisters' Christmas morning.\n";

  const files: Record<string, string> = {
    ...sceneFiles,
    "Chapters/Part First/index.md": "---\nkind: act\n---\n",
    "Chapters/Part Second/index.md": "---\nkind: act\n---\n",
    "Notes/Plot threads.md":
      "# Plot threads\n\n- Four sisters coming of age during the Civil War\n- Jo's writing ambition vs. family duty\n- Laurie as neighbor, friend, and almost-more\n- Beth's quiet strength and fragile health\n",
    "Notes/Tone.md":
      "# Tone\n\nWarm domestic realism with moral seriousness. Keep Jo's voice brisk; let Beth stay soft without vanishing.\n",
    "Notes/Source.md":
      "# Source\n\nText from [Project Gutenberg ebook #37106](https://www.gutenberg.org/ebooks/37106), *Little Women; Or, Meg, Jo, Beth, and Amy* by Louisa May Alcott. Public domain in the USA. Illustrations omitted. Lightly cleaned for the Zephyrwrite demo (paragraphs, chapter markers).\n",
    [`Notes/${parkedName}`]: parkedBody,
    "Database/Jo March.md":
      "# Jo March\n\nThe second March sister. Writes plays, burns manuscripts, and refuses to be ladylike on schedule.\n",
    "Database/Meg March.md":
      "# Meg March\n\nThe eldest. Loves pretty things, carries the family's dignity, and learns the cost of both.\n",
    "Database/Beth March.md":
      "# Beth March\n\nShy, musical, and the moral center of the household.\n",
    "Database/Amy March.md":
      "# Amy March\n\nYoungest sister, artist-in-training, and careful student of manners.\n",
    "Database/Laurie Laurence.md":
      "# Laurie Laurence\n\nThe boy next door. Grandfather, piano, and a great deal of feeling.\n",
    "Database/March Home.md":
      "# March Home\n\nThe March family parlor and the emotional map of the novel.\n",
  };

  const databaseFields: Record<string, EntityFrontmatter> = {
    "Jo March.md": {
      ...emptyEntityFrontmatter(),
      type: just("character"),
      role: just("Protagonist"),
      summary: just("Writer, sister, and restless center of Part First."),
      tags: ["pov", "demo"],
      aka: ["Jo"],
      links: [
        {
          id: "link-jo-laurie",
          file: "Laurie Laurence.md",
          relation: "friends",
          note: just("Neighbors, collaborators, almost engaged"),
        },
        {
          id: "link-jo-meg",
          file: "Meg March.md",
          relation: "sisters",
          note: just("Eldest confidante"),
        },
      ],
      arc: [
        {
          id: "arc-demo-1",
          order: 1,
          at: just("Opening"),
          moment: "Christmas without presents",
          link: just({ file: "Chapters/Playing Pilgrims/scene-a0001.md" }),
        },
        {
          id: "arc-demo-2",
          order: 2,
          at: just("Midpoint"),
          moment: "Neighborly with Laurie",
          link: just({ file: "Chapters/The Laurence Boy/scene-0003.md" }),
        },
        {
          id: "arc-demo-3",
          order: 3,
          at: just("Crisis"),
          moment: "Heartache with Laurie",
          link: just({ file: "Chapters/Heartache/scene-c0001.md" }),
        },
      ],
    },
    "Meg March.md": {
      ...emptyEntityFrontmatter(),
      type: just("character"),
      role: just("Eldest sister"),
      summary: just("Responsible, romantic, and often tired."),
      tags: ["demo"],
      aka: ["Meg"],
      links: [
        {
          id: "link-meg-jo",
          file: "Jo March.md",
          relation: "sisters",
          note: nothing,
        },
      ],
    },
    "Beth March.md": {
      ...emptyEntityFrontmatter(),
      type: just("character"),
      role: just("Sister"),
      summary: just("Gentle heart of the March home."),
      tags: ["demo"],
      aka: ["Beth"],
    },
    "Amy March.md": {
      ...emptyEntityFrontmatter(),
      type: just("character"),
      role: just("Youngest sister"),
      summary: just("Artist with exacting taste."),
      tags: ["demo"],
      aka: ["Amy"],
    },
    "Laurie Laurence.md": {
      ...emptyEntityFrontmatter(),
      type: just("character"),
      role: just("Neighbor"),
      summary: just("The Laurence boy across the way."),
      tags: ["demo"],
      aka: ["Laurie", "Teddy"],
      links: [
        {
          id: "link-laurie-jo",
          file: "Jo March.md",
          relation: "friends",
          note: just("Piano, plays, and proposals"),
        },
      ],
    },
    "March Home.md": {
      ...emptyEntityFrontmatter(),
      type: just("place"),
      summary: just("Where the four sisters grow up."),
      tags: ["setting", "demo"],
    },
  };

  const firstScene = "Chapters/Playing Pilgrims/scene-a0001.md";
  const firstContent = files[firstScene] ?? "";
  const history: Record<string, DemoHistoryEntry[]> = {
    [firstScene]: [
      {
        oid: "demo1",
        message: "Create project",
        time: 1_723_363_200,
        before: "",
        after: firstContent.slice(0, 120),
      },
      {
        oid: "demo2",
        message: "Autosave",
        time: 1_723_366_800,
        before: firstContent.slice(0, 120),
        after: firstContent.slice(0, 400),
      },
      {
        oid: "demo3",
        message: "Autosave",
        time: 1_723_370_400,
        before: firstContent.slice(0, 400),
        after: firstContent,
      },
    ],
  };

  const deleted: DemoDeletedEntry[] = [
    {
      relativePath: "Notes/Old outline.md",
      oid: "demod1",
      message: "Delete note: Old outline.md",
      time: 1_723_350_000,
      content: "# Old outline\n\nScrapped outline from an earlier pass.\n",
    },
  ];

  const unplaced: UnplacedItem[] = [
    {
      kind: "scene",
      name: parkedName,
      title: "",
      synopsis: "Draft moment set aside from the manuscript.",
      scenes: [],
    },
  ];

  cachedSeed = just({
    rootPath: demoProjectRoot(),
    config,
    files,
    databaseFields,
    graphLayout: {
      "Jo March.md": { x: 80, y: 120 },
      "Meg March.md": { x: 280, y: 60 },
      "Beth March.md": { x: 280, y: 180 },
      "Amy March.md": { x: 280, y: 300 },
      "Laurie Laurence.md": { x: 480, y: 120 },
      "March Home.md": { x: 180, y: 360 },
    },
    history,
    deleted,
    unplaced,
  });
  return cachedSeed.value;
}

/** Test helper: drop the memoized seed so the next build reloads content. */
export function clearDemoFixtureCache(): void {
  cachedSeed = nothing;
}
